export const APP_VERSION = "V:10082026B1";

export const RELEASE_NOTES = [
  {
    version: "V:10082026B1",
    releaseDate: "Oct 08, 2026",
    sections: [
      {
        title: "Overview",
        description:
          "Next TAP2 release focused on eligible-deal validation v2, TDRO datamart performance, E2E exception lifecycle and past end-date behavior, offer lifecycle fixes, and PPM grid UX improvements.",
      },
      {
        title: "TPR licensing and eligible deals",
        items: [
          "Skip creating licensing jobs when PMX TPR end date is already past at ingest; log E2E with in-the-past context (RIGHTS-30472).",
          "Eligible-deal validation v2: full-data E2E exceptions with release, licensing, and deal context (RIGHTS-30514).",
          "When eligible-deal scan returns empty, distinguish no TPR rate card on valid deals vs no deals matching licensee (RIGHTS-30659).",
          "Rate card eligibility and splitter strategy updates (RIGHTS-30812).",
          "Avoid duplicate rate-card E2E register/resolve between splitter and eligible-deal validator (RIGHTS-31085).",
        ],
      },
      {
        title: "E2E exceptions",
        items: [
          "Stop auto-dismissing TPR E2E exceptions when the relevant end date is in the past so exceptions remain visible for investigation (RIGHTS-30471).",
          "Gate or suppress format-related E2E when TDRO coverage applies; ignore empty tier-alias formats on rate cards (RIGHTS-30516).",
          "Do not create new E2E exceptions when context-specific end date is already past.",
          "Improved resolution for LICENSING_DEAL_HAS_TPR_RATE_CARD_NOT_IN_TAP2 and format vs deal rate card matching.",
          "TPR Exceptions page query performance improvements (RIGHTS-30592).",
        ],
      },
      {
        title: "TDRO and Update Engine",
        items: [
          "Redis cache for TDRO lookup per deal and catalog to reduce repeated datamart hits (RIGHTS-30716).",
          "Defer AUTOUPDATE while licensing jobs are active (RIGHTS-30716).",
          "Skip creating offers whose term end is in the past; mark PROPOSED_INVALID; term ending today is still allowed (RIGHTS-30783, RIGHTS-30784).",
          "Update Engine shields for past end-date offer behavior (RIGHTS-30784).",
        ],
      },
      {
        title: "Offers",
        items: [
          "Rightsline push (single and bulk) records Created By as the logged-in user, not api-user.",
          "Remove superseded unpushed Missing Pricing offers on update (RIGHTS-30910).",
          "Do not mark original offers as Duplicate (RIGHTS-30970).",
        ],
      },
      {
        title: "PPM and PPT",
        items: [
          "Save current promotion list filters as default PPM user preferences; apply on first visit to PPM home with matching URL; PPM preference API refactored for per-preference updates (RIGHTS-31007).",
          "PPT promo-related grids (promo list, discount details, audit log): sensible initial column widths; replace restrictive minWidth with fixed width so users can resize columns manually (RIGHTS-31039).",
        ],
      },
    ],
  },
  {
    version: "V:09282026B1",
    releaseDate: "Sept 28, 2026",
    sections: [
      {
        title: "Overview",
        description:
          "Fast-follow release with grid customization, filter stability improvements, and Update Engine bug fixes.",
      },
      {
        title: "Grid Features",
        items: [
          "Grid column layout customization is now persistent across sessions; users can resize, reorder, hide, and pin columns, and their preferences are saved automatically (RIGHTS-30641).",
        ],
      },
      {
        title: "Filters",
        items: [
          "Offers filter sidebar now initializes correctly when navigating directly to /offers?jobId= URLs, resolving an issue where filters would not load on first page load.",
          "Promos grid filter panel no longer closes unexpectedly after user interaction, maintaining filter state during grid operations (RIGHTS-30676).",
          "PMX Ireland territory filter now correctly filters PMX data for Ireland region (RAMS-5630).",
        ],
      },
      {
        title: "Update Engine",
        items: [
          "Fixed bug where Update Engine was incorrectly creating Amazon offers on National TPR licensing jobs, preventing inappropriate offer generation for territory-mismatched deals (RIGHTS-31008).",
        ],
      },
    ],
  },
  {
    version: "V:09172026B1",
    releaseDate: "Sept 17, 2026",
    sections: [
      {
        title: "Overview",
        description:
          "Fast-follow release for licensing Job View stability and catalog pill display (RIGHTS-30886).",
      },
      {
        title: "Licensing jobs",
        items: [
          "Job View modal no longer white-screens with React error #31 when task rows include API reference objects ({ licensingJobId, catalogId } or { licensingJobId, dealId }); Job, Details, and Status columns render display-safe strings via dedicated cell renderers and row normalization before the grid loads.",
          "Licensing job task rows are scalarized on fetch so AG Grid and React never receive raw reference payloads as cell values.",
          "Multi-line Message cells sort Processing N of M status lines by deal progress index (N ascending) so polled updates read in sequence instead of arrival order.",
          "Catalog pills truncate long titles without clipping the catalog ID; territory pills show full names. Expanded catalog and territory pills stay open across grid refresh.",
          "Catalog pill labels no longer show truncated titles such as X (83595) when the API returns title text with an embedded ID.",
        ],
      },
    ],
  },
  {
    version: "V:09092026B1",
    releaseDate: "Sept 09, 2026",
    sections: [
      {
        title: "Overview",
        description:
          "Fast-follow release focused on licensing job reliability, TPR exception performance, and bulk upload stability.",
      },
      {
        title: "Licensing jobs",
        items: [
          "Bulk licensing job file upload now runs asynchronously and returns a job tracker ID immediately, avoiding gateway timeouts on large files; validation results are available via bulkIngestResult.",
          "Post-restart licensing job resume is centralized with atomic restart claims, preventing duplicate resume runs and progress counts exceeding target totals.",
          "Concurrent catalog-large licensing jobs (≥30 catalogs) are capped at two so smaller queued jobs are not starved; queue ordering uses a new queued_at timestamp for FIFO within each tier.",
          "Splitter throughput improved by deduplicating licensee contact Rightsline lookups per deal and removing per-deal cache eviction during split runs.",
          "Duplicate licensing jobs from near-simultaneous TPR release Kafka events for the same pmxReleaseId are prevented by keeping only the highest-offset event per release.",
        ],
      },
      {
        title: "TPR Exceptions",
        items: [
          "Show Mine roll-up avoids redundant per-batch fetches when mineOnly=true, improving response time for owned exceptions.",
          "TPR email distribution detail data is cached in Redis to avoid reloading on every Show Mine request.",
        ],
      },
      {
        title: "Users API",
        items: [
          "Tap Admin role can access user list endpoints; v1 users API returns full paginated Page metadata for filter dropdown support.",
        ],
      },
    ],
  },
  {
    version: "V:090226B1",
    releaseDate: "Sept 02, 2026",
    sections: [
      {
        title: "Overview",
        description:
          "This release delivers the TPR Exceptions milestone: improved exception detection and filtering, a more reliable Update Engine, splitter and search fixes, TDRO workflow support, and operational observability improvements.",
      },
      {
        title: "Modern TAP UI",
        description:
          "TAP UI has been modernized with Disney's Spellbook design system, delivering a refreshed experience with improved usability and consistency across the application.",
        items: [
          "Spellbook integration brings a modern, consistent Disney design language to TAP screens, navigation, and components.",
          "New PPM interface for Promo Campaigns, supporting end-to-end promo planning and management workflows.",
          "Additional UI enhancements across licensing, offers, exceptions, and operational workflows.",
        ],
      },
      {
        title: "Promo Planning Module (PPM) Overview",
        description:
          "This release delivers the launch of the Promo Planning Module (PPM): review all promos / releases in one location and download client forms.",
      },
      {
        title: "Promo Planning Module (PPM) New capabilities",
        items: [
          "Review all releases / promos created by users; filter and sort by promo type, start & end date in addition to region",
          "Review individual promos and ability to automatically download client forms for applicable accounts per promo",
          "Review issues / warnings related to TPR client IDs that don't exist in Xavier",
        ],
      },
      {
        title: "New capabilities",
        items: [
          "TPR Exceptions: expanded exception handling across splitter and eligibility flows, corrected Dismissed By filtering, and fixes for exception email notifications.",
          "Update Engine: unified relevance policy for smarter deal-change detection, fix for jobs stuck in RUNNING after tasks complete, and improved handling when deals have no language configured.",
          "TDRO workflow support after offer push to Rightsline, with bulk delete and push performance improvements.",
          "Snowflake Beast private key authentication and configurable schema for datamart (RIGHTS-29305).",
          "Datadog JSON logging and Micrometer metrics for tap-jobs.",
        ],
      },
    ],
  },
];
